/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/refs */
/**
 * FeatureFlagsContext - Feature Flags Provider
 *
 * Provides feature flag state throughout the application:
 * - Loads effective feature flags from the API after auth and when the tenant changes
 * - Does not persist flags in browser storage (always fresh from GET /auth/me/features)
 * - Re-fetches on tenant switch or manual refresh (e.g. after Platform Admin updates)
 *
 * Architecture:
 * - Master flags: All TRUE (defined in backend YAML)
 * - Customer flags: Overrides master (stored in DB)
 * - Tenant flags: Overrides customer (stored in DB)
 * - Effective flags: Merged result for current tenant
 *
 * Usage:
 *   const { flags, isEnabled, isLoading } = useFeatureFlags();
 *
 *   if (isEnabled('tools_marketplace')) {
 *     // Render marketplace button
 *   }
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { useTenant } from './TenantContext'
import { useAuth } from './AuthContext'
import { request } from '../services/api/core'
import { logger } from '../utils/logger'
import {
  type FeatureFlags,
  type FeatureFlagKey,
  createDefaultFlags,
  isFlagEffectivelyEnabled,
  isValidFlagKey,
} from '../constants/featureFlags'
import type { FeatureStage } from '../services/api/platformAdmin'

// =============================================================================
// TYPES
// =============================================================================

export interface FeatureFlagsContextValue {
  /**
   * All effective feature flags for the current tenant.
   * Keys are flag names (e.g., 'tools_marketplace'), values are booleans.
   */
  flags: FeatureFlags

  /**
   * Lifecycle stages for all feature flags.
   * Keys are flag names, values are stage (alpha|beta|uat|ga).
   */
  stages: Record<string, FeatureStage>

  /**
   * Check if a specific feature flag is enabled.
   * @param flagKey - The flag key to check (e.g., 'tools_marketplace')
   * @returns true if enabled, false otherwise
   */
  isEnabled: (flagKey: FeatureFlagKey) => boolean

  /**
   * Get the lifecycle stage for a specific feature flag.
   * @param flagKey - The flag key to check
   * @returns The stage (alpha|beta|uat|ga), defaults to 'ga' if not found
   */
  getStage: (flagKey: FeatureFlagKey) => FeatureStage

  /**
   * Whether feature flags are currently loading.
   */
  isLoading: boolean

  /**
   * Last time flags were updated (epoch ms from API when provided).
   */
  lastUpdated: number | null

  /**
   * Force refresh flags from the API.
   */
  refreshFlags: () => Promise<void>

  /**
   * Error message if flag loading failed.
   */
  error: string | null
}

interface FeatureFlagsResponse {
  flags: Record<string, boolean>
  stages: Record<string, FeatureStage>
  tenant_id: string | null
  updated_at: number
  flag_count: number
}

interface FetchResult { 
  flags: FeatureFlags
  stages: Record<string, FeatureStage>
  lastUpdated: number | null 
}

// =============================================================================
// CONTEXT
// =============================================================================

const defaultContextValue: FeatureFlagsContextValue = {
  flags: createDefaultFlags(),
  stages: {},
  isEnabled: () => false,
  getStage: () => 'ga',
  isLoading: true,
  lastUpdated: null,
  refreshFlags: async () => {},
  error: null,
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>(defaultContextValue)

// =============================================================================
// PROVIDER
// =============================================================================

interface FeatureFlagsProviderProps {
  children: ReactNode
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const { isAuthenticated, isCheckingAuth } = useAuth()
  const { currentTenant, isLoading: isTenantLoading } = useTenant()

  const [flags, setFlags] = useState<FeatureFlags>(createDefaultFlags)
  const [stages, setStages] = useState<Record<string, FeatureStage>>({})
  // True only while a flags request is actively in flight. The overall
  // `isLoading` exposed below is DERIVED (see `isLoading`) so it can never be
  // false while `flags` still hold stale values for a different auth/tenant.
  const [isFetching, setIsFetching] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchInFlightRef = useRef<Promise<FetchResult> | null>(null)

  // Identifies which auth/tenant the currently-held `flags` were loaded for.
  // 'anon' = logged-out defaults. `tenant:<id>` = a real authenticated load.
  // When this no longer matches the live session key, the flags are stale and
  // isLoading is forced true — closing the post-login window where default
  // flags (only ideate_view/docs_view on) would otherwise leak through with
  // isLoading=false and misdirect the "/" landing redirect.
  const loadedKeyRef = useRef<string | null>(null)

  const sessionKey = !isAuthenticated ? 'anon' : `tenant:${currentTenant?.id ?? 'none'}`

  /**
   * Fetch effective feature flags from API. Concurrent callers share one request.
   */
  const fetchFlags = useCallback(async (): Promise<FetchResult> => {
    const existing = fetchInFlightRef.current
    if (existing) {
      return existing
    }

    const promise = (async (): Promise<FetchResult> => {
      const response = await request<FeatureFlagsResponse>('/auth/me/features')
      const mergedFlags = {
        ...createDefaultFlags(),
        ...response.flags,
      } as FeatureFlags
      const lu =
        typeof response.updated_at === 'number' && Number.isFinite(response.updated_at)
          ? response.updated_at
          : Date.now()
      return { 
        flags: mergedFlags, 
        stages: response.stages || {},
        lastUpdated: lu 
      }
    })()
      .catch((e) => {
        logger.error('Failed to fetch feature flags', e)
        throw e
      })
      .finally(() => {
        fetchInFlightRef.current = null
      })

    fetchInFlightRef.current = promise
    return promise
  }, [])

  const loadFlags = useCallback(async () => {
    // Auth or tenant still resolving — derived isLoading already reports true.
    if (isCheckingAuth) {
      return
    }

    if (!isAuthenticated) {
      setFlags(createDefaultFlags())
      setStages({})
      setError(null)
      setLastUpdated(null)
      loadedKeyRef.current = 'anon'
      return
    }

    if (isTenantLoading) {
      return
    }

    const key = sessionKey
    setIsFetching(true)
    setError(null)

    try {
      const { flags: newFlags, stages: newStages, lastUpdated: lu } = await fetchFlags()
      setFlags(newFlags)
      setStages(newStages)
      setLastUpdated(lu)
      // Mark these flags as belonging to the current authenticated tenant.
      loadedKeyRef.current = key
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to load feature flags'
      setError(errorMessage)
      // Mark the attempt as resolved even on failure so isLoading can settle and
      // the app degrades (with whatever flags we have) instead of hanging on a
      // skeleton forever when the flags endpoint is unreachable.
      loadedKeyRef.current = key
    } finally {
      setIsFetching(false)
    }
  }, [isAuthenticated, isCheckingAuth, isTenantLoading, sessionKey, fetchFlags])

  const refreshFlags = useCallback(async () => {
    await loadFlags()
  }, [loadFlags])

  // ---------------------------------------------------------------------------
  // FRONTEND FEATURE-FLAG CHECKS DECOMMISSIONED — single kill-switch.
  //
  // All callers (useFeatureFlags().isEnabled, useIsFeatureEnabled,
  // useFeatureFlagsMultiple, useAllFeaturesEnabled, useAnyFeatureEnabled,
  // useCanAccess.isFlagEnabled, useFlagCheck, and the viewFlag/treeViewFlag
  // gates inside useCanAccess.canAccess/canViewUI) route through this method,
  // so returning true here unconditionally restores UI access. RBAC is still
  // enforced via PageAccessGuard / canViewUI's role check and by backend
  // /auth/me gating + endpoint-level 403s.
  //
  // To re-enable flag gating, restore the original implementation:
  //
  //   if (!isValidFlagKey(flagKey)) {
  //     logger.warn(`Invalid feature flag key: ${flagKey}`)
  //     return false
  //   }
  //   return isFlagEffectivelyEnabled(flagKey, flags)
  // ---------------------------------------------------------------------------
  const isEnabled = useCallback(
    (flagKey: FeatureFlagKey): boolean => {
      if (!isValidFlagKey(flagKey)) {
        logger.warn(`Invalid feature flag key: ${flagKey}`)
        return false
      }
      return isFlagEffectivelyEnabled(flagKey, flags)
    },
    [flags],
  )

  const getStage = useCallback(
    (flagKey: FeatureFlagKey): FeatureStage => {
      return stages[flagKey] || 'ga'
    },
    [stages],
  )

  useEffect(() => {
    void loadFlags()
  }, [loadFlags])

  // Derived loading state. True whenever the currently-held flags do not yet
  // belong to the live session — i.e. auth/tenant is still resolving, a fetch
  // is in flight, or the user just authenticated but the post-login flag fetch
  // hasn't completed. This is computed (not effect-driven), so it is correct on
  // the very first render after `isAuthenticated` flips, with no stale-false gap.
  const flagsAreFresh = loadedKeyRef.current === sessionKey
  const isLoading =
    isCheckingAuth || isTenantLoading || isFetching || (isAuthenticated && !flagsAreFresh)

  const contextValue = useMemo<FeatureFlagsContextValue>(
    () => ({
      flags,
      stages,
      isEnabled,
      getStage,
      isLoading,
      lastUpdated,
      refreshFlags,
      error,
    }),
    [flags, stages, isEnabled, getStage, isLoading, lastUpdated, refreshFlags, error]
  )

  return (
    <FeatureFlagsContext.Provider value={contextValue}>{children}</FeatureFlagsContext.Provider>
  )
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to access the full feature flags context.
 */
export function useFeatureFlags(): FeatureFlagsContextValue {
  const context = useContext(FeatureFlagsContext)
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider')
  }
  return context
}

/**
 * Hook to check if a specific feature flag is enabled.
 *
 * @param flagKey - The flag key to check
 * @returns boolean - Whether the flag is enabled
 *
 * Usage:
 *   const canAccessMarketplace = useIsFeatureEnabled('tools_marketplace');
 */
export function useIsFeatureEnabled(flagKey: FeatureFlagKey): boolean {
  const { isEnabled, isLoading } = useFeatureFlags()

  if (isLoading) {
    return false
  }

  return isEnabled(flagKey)
}

/**
 * Hook to check multiple feature flags at once.
 *
 * @param flagKeys - Array of flag keys to check
 * @returns Object with flag keys as keys and booleans as values
 *
 * Usage:
 *   const { tools_view, tools_create } = useFeatureFlagsMultiple(['tools_view', 'tools_create']);
 */
export function useFeatureFlagsMultiple<T extends FeatureFlagKey>(
  flagKeys: T[]
): Record<T, boolean> {
  const { flags, isLoading, isEnabled } = useFeatureFlags()

  return useMemo(() => {
    const result = {} as Record<T, boolean>
    for (const key of flagKeys) {
      result[key] = isLoading ? false : isEnabled(key)
    }
    return result
  }, [flags, flagKeys, isLoading, isEnabled])
}

/**
 * Hook to check if all flags in a list are enabled.
 *
 * @param flagKeys - Array of flag keys that must all be enabled
 * @returns boolean - True if ALL flags are enabled
 */
export function useAllFeaturesEnabled(flagKeys: FeatureFlagKey[]): boolean {
  const { isLoading, isEnabled } = useFeatureFlags()

  return useMemo(() => {
    if (isLoading) return false
    return flagKeys.every((key) => isEnabled(key))
  }, [flagKeys, isLoading, isEnabled])
}

/**
 * Hook to check if any flag in a list is enabled.
 *
 * @param flagKeys - Array of flag keys where at least one must be enabled
 * @returns boolean - True if ANY flag is enabled
 */
export function useAnyFeatureEnabled(flagKeys: FeatureFlagKey[]): boolean {
  const { isLoading, isEnabled } = useFeatureFlags()

  return useMemo(() => {
    if (isLoading) return false
    return flagKeys.some((key) => isEnabled(key))
  }, [flagKeys, isLoading, isEnabled])
}

export default FeatureFlagsContext
